import { apiFetch, apiFetchBlob } from '../lib/api';

export type ChatKind = 'SUPPORT' | 'ORDER';
export type ComplaintStatus = 'NONE' | 'OPEN' | 'RESOLVED';
export type ChatScope = 'all' | 'support' | 'order' | 'complaint';

export interface ChatListItem {
  id: string;
  kind: ChatKind;
  title: string;
  preview: string | null;
  unread: boolean;
  complaintStatus: ComplaintStatus;
  orderId: string | null;
  orderNumber: string | null;
  orderStatus: string | null;
  buyerName: string | null;
  purged: boolean;
  lastMessageAt: string | null;
}

export interface ChatAttachment {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
}

export interface ChatMessage {
  id: string;
  kind: 'TEXT' | 'FILE' | 'SYSTEM';
  text: string | null;
  senderId: string | null;
  mine: boolean;
  createdAt: string;
  attachments: ChatAttachment[];
}

export const chatApi = {
  unread: () => apiFetch<{ count: number }>('/chats/unread'),

  list: (scope: ChatScope = 'all', cursor?: string) => {
    const query = new URLSearchParams();
    if (scope !== 'all') query.set('scope', scope);
    if (cursor) query.set('cursor', cursor);
    const suffix = query.toString();
    return apiFetch<{ items: ChatListItem[]; nextCursor?: string }>(`/chats${suffix ? `?${suffix}` : ''}`);
  },

  openSupport: () => apiFetch<ChatListItem>('/chats/support', { method: 'POST' }),

  openOrder: (orderId: string) => apiFetch<ChatListItem>(`/chats/order/${orderId}`),

  get: (chatId: string) => apiFetch<ChatListItem>(`/chats/${chatId}`),

  messages: (chatId: string, cursor?: { before?: string; after?: string }) => {
    const query = new URLSearchParams();
    if (cursor?.before) query.set('before', cursor.before);
    if (cursor?.after) query.set('after', cursor.after);
    const suffix = query.toString();
    return apiFetch<{ items: ChatMessage[]; olderCursor?: string }>(
      `/chats/${chatId}/messages${suffix ? `?${suffix}` : ''}`,
    );
  },

  send: (chatId: string, body: { text?: string; dataUrl?: string; filename?: string }) =>
    apiFetch<ChatMessage>(`/chats/${chatId}/messages`, { method: 'POST', body }),

  read: (chatId: string) => apiFetch<{ ok: true }>(`/chats/${chatId}/read`, { method: 'POST' }),

  complain: (chatId: string, reason: string) =>
    apiFetch<ChatListItem>(`/chats/${chatId}/complaint`, { method: 'POST', body: { reason } }),

  resolve: (chatId: string) => apiFetch<ChatListItem>(`/chats/${chatId}/complaint/resolve`, { method: 'POST' }),

  remove: (chatId: string) => apiFetch<{ ok: true }>(`/chats/${chatId}`, { method: 'DELETE' }),

  file: (attachmentId: string) => apiFetchBlob(`/chats/files/${attachmentId}`),
};
